import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaExclamationTriangle, FaShieldAlt, FaChartLine, FaUserShield, FaBan } from 'react-icons/fa';
import MentorProfile from './MentorProfile';
import Testimonials from './Testimonials';
import WebinarSchedule from './WebinarSchedule';
import Pricing from './Pricing';
import CTA from './CTA';
import FloatingCTA from './FloatingCTA';
import { saveUTMParams, trackViewContent } from '../utils/tracking';

function LP2() {
  useEffect(() => {
    saveUTMParams();
    trackViewContent({
      content_name: 'News Hack LP2 - Stop Loss Pain',
      content_category: 'Trading Course',
      content_type: 'landing_page_variant_2'
    });
  }, []);

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section - Pain Point Angle */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-darkBlue via-darkerBlue to-darkBlue text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 text-blue-500 text-[300px] font-bold">!</div>
          <div className="absolute bottom-20 left-20 text-blue-500 text-[300px] font-bold">×</div>
        </div>

        <div className="container max-w-6xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <motion.div 
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-6 py-3 mb-8"
            >
              <FaExclamationTriangle className="text-blue-400" />
              <span className="text-blue-400 font-bold uppercase tracking-wider">Waktunya Berubah!</span>
              <FaExclamationTriangle className="text-blue-400" />
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Berapa Kali Lagi Anda Mau<br/>
              <span className="text-blue-400 text-5xl md:text-6xl lg:text-7xl">KENA SL SAAT NEWS?</span>
            </h1>

            <div className="max-w-4xl mx-auto mb-8">
              <p className="text-xl md:text-2xl mb-6 text-textGray">
                NFP rilis → Market spike → <span className="text-blue-400 font-bold">Stop Loss kena!</span><br/>
                CPI announce → Slippage gila → <span className="text-blue-400 font-bold">Floating minus!</span><br/>
                FOMC statement → Whipsaw brutal → <span className="text-blue-400 font-bold">MC dalam sekejap!</span>
              </p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-r from-blue-900/40 to-blue-800/40 border-2 border-blue-500/50 rounded-2xl p-8"
              >
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-blue-400">
                  FAKTA MENGEJUTKAN:
                </h3>
                <p className="text-lg md:text-xl text-white">
                  <span className="text-blue-400 font-bold">87% trader Indonesia</span> mengalami loss terbesar mereka saat trading news.
                  <span className="block mt-3 text-yellow-400 font-bold">
                    Padahal justru di saat news-lah profit terbesar menanti!
                  </span>
                </p>
              </motion.div>
            </div>

            <motion.a
              href="/checkout"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block bg-gradient-to-r from-primaryDark to-primary hover:from-primary hover:to-primaryLight text-darkBlue font-bold py-5 px-12 rounded-full text-xl md:text-2xl shadow-2xl"
            >
              Stop Jadi Korban News! Pelajari Caranya →
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Pain Points Deep Dive */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-darkerBlue to-darkBlue text-white">
        <div className="container max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Kenali <span className="text-blue-400">Tantangan Utama</span> Anda Saat Trading News
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 rounded-xl p-6 border border-blue-500/30"
            >
              <FaBan className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-bold mb-3 text-blue-400">Slippage Mematikan</h3>
              <p className="text-textGray mb-4">
                Entry di 1900, eksekusi di 1920. <span className="text-blue-400 font-semibold">Loss 20 pips sebelum mulai!</span>
              </p>
              <p className="text-sm text-yellow-400">
                ✓ News Hack ajarkan teknik "Zero Slippage Entry"
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 rounded-xl p-6 border border-blue-500/30"
            >
              <FaExclamationTriangle className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-bold mb-3 text-blue-400">Whipsaw Brutal</h3>
              <p className="text-textGray mb-4">
                Naik 50 pips, turun 100 pips dalam 30 detik. <span className="text-blue-400 font-semibold">SL pasti kena!</span>
              </p>
              <p className="text-sm text-yellow-400">
                ✓ News Hack gunakan "Dynamic Stop Protection"
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 rounded-xl p-6 border border-blue-500/30"
            >
              <FaChartLine className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-bold mb-3 text-blue-400">Fake Breakout</h3>
              <p className="text-textGray mb-4">
                Break resistance, Anda buy. <span className="text-blue-400 font-semibold">Ternyata fake, balik arah!</span>
              </p>
              <p className="text-sm text-yellow-400">
                ✓ News Hack pakai "Momentum Confirmation Filter"
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 rounded-xl p-6 border border-blue-500/30"
            >
              <FaUserShield className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-bold mb-3 text-blue-400">Psikologi Hancur</h3>
              <p className="text-textGray mb-4">
                Trauma trading news. <span className="text-blue-400 font-semibold">Takut entry, malah nonton profit lewat!</span>
              </p>
              <p className="text-sm text-yellow-400">
                ✓ News Hack bangun "Mental Framework Samurai"
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 bg-gradient-to-b from-darkBlue to-darkerBlue text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-green-900/20 to-green-800/20 rounded-2xl p-8 md:p-12 border border-green-500/30"
          >
            <FaShieldAlt className="text-5xl text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-green-400">
              Ada Cara Lebih Baik!
            </h3>
            <p className="text-lg md:text-xl text-textGray mb-6">
              Bayangkan trading news dengan tenang, tanpa takut SL, tanpa stress whipsaw.
              <span className="text-primary font-bold block mt-3">
                Malah MEMANFAATKAN volatilitas untuk profit maksimal!
              </span>
            </p>
            <p className="text-base text-textGray mb-8">
              Itulah yang dilakukan murid-murid News Hack. Mereka yang dulu trauma, sekarang hunting news release!
            </p>
            <motion.a
              href="/checkout"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block bg-gradient-to-r from-primaryDark to-primary hover:from-primary hover:to-primaryLight text-darkBlue font-bold py-4 px-10 rounded-full text-lg md:text-xl shadow-2xl"
            >
              Saya Mau Berhenti Rugi Saat News! →
            </motion.a>
          </motion.div>
        </div>
      </section>

      <MentorProfile />
      <Testimonials />
      <WebinarSchedule />
      <Pricing />
      <CTA />
      <FloatingCTA />
    </div>
  );
}

export default LP2;